import React, { useState } from 'react';
import useSWR from 'swr';
import { MapPin } from 'lucide-react';
import * as P from '@/lib/portal/service';

// Status wording shown to clients (verified project status only; no progress figures).
const STATUS = { draft: 'Planning', active: 'On site', on_hold: 'On hold', completed: 'Complete', archived: 'Archived' };
export const statusLabel = (s) => STATUS[s] || (s ? s.replace(/_/g, ' ') : '—');

// Hero photograph = most recent photograph from a PUBLISHED progress update (RLS already restricts clients to published rows).
export function useHeroPhoto(projectId) {
  const upd = useSWR(projectId ? ['updates', projectId] : null, () => P.updates(projectId));
  const photo = (upd.data || []).filter((u) => u.published).flatMap((u) => u.progress_update_photos || [])[0];
  const url = useSWR(photo ? ['photo', photo.storage_path] : null, () => P.previewUrl(photo.storage_path));
  return { photo, url: url.data, loading: upd.isLoading };
}

// Architectural placeholder — deliberately not a stock photograph.
function Placeholder() {
  return (
    <div className="absolute inset-0 bg-[#3A3936]" data-testid="hero-placeholder">
      <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: 'linear-gradient(#F7F5F1 1px, transparent 1px), linear-gradient(90deg, #F7F5F1 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
      <div className="absolute top-4 right-5 text-[10px] uppercase tracking-[0.25em] text-[#B5AFA5]">Photography to follow</div>
    </div>
  );
}

export default function ProjectHero({ p, size = 'card', children }) {
  const { url } = useHeroPhoto(p.id);
  const [broken, setBroken] = useState(false);
  const h = size === 'page' ? 'h-[300px] md:h-[420px]' : 'h-[220px] md:h-[260px]';
  return (
    <div className={`relative overflow-hidden ${h} ${size === 'page' ? 'rounded-2xl' : 'rounded-t-2xl'}`} data-testid={`project-hero-${p.project_code}`}>
      {url && !broken ? <img src={url} alt="" onError={() => setBroken(true)} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /> : <Placeholder />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#3A3936]/90 via-[#3A3936]/30 to-transparent" />
      <div className={`absolute inset-x-0 bottom-0 ${size === 'page' ? 'p-6 md:p-10' : 'p-6'} text-[#F7F5F1]`}>
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#D8D0C4]">{p.project_code}</div>
            <h2 className={`font-display font-semibold tracking-tight mt-1 leading-[1.05] ${size === 'page' ? 'text-3xl md:text-5xl' : 'text-2xl md:text-3xl'}`}>{p.name}</h2>
            {p.site_address && <div className="mt-2 text-sm text-[#D8D0C4] inline-flex items-center gap-1.5"><MapPin size={13} /> {p.site_address}</div>}
          </div>
          <span className="shrink-0 rounded-full border border-[#F7F5F1]/40 bg-[#3A3936]/40 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-[0.18em]" data-testid="hero-status">{statusLabel(p.status)}</span>
        </div>
        {children}
      </div>
    </div>
  );
}
